import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Home, Search, User, Heart, Users, Building, FileCheck, ClipboardList, Clipboard, GraduationCap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteContext } from '@/contexts/SiteContext';

export function MobileBottomNav() {
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const { site } = useSiteContext();

  // CRO navigation (for CRO white-label subdomain)
  const croNavItems = [
    { icon: Home, label: 'Command', path: '/cro/command-center' },
    { icon: Users, label: 'Orders', path: '/cro-orders' },
    { icon: GraduationCap, label: 'Academy', path: '/academy' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  // Trial site navigation (for trial site white-label subdomain)
  const trialSiteNavItems = [
    { icon: Home, label: 'Dashboard', path: site?.id ? `/trial-site/${site.id}` : '/site-dashboard' },
    { icon: Users, label: 'Patients', path: site?.id ? `/trial-site/${site.id}?tab=patients` : '/site-dashboard' },
    { icon: GraduationCap, label: 'Academy', path: '/academy' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  // Patient navigation
  const patientNavItems = [
    { icon: Home, label: 'Home', path: '/patient-dashboard' },
    { icon: Search, label: 'Trials', path: '/trials' },
    { icon: Heart, label: 'Matches', path: '/matches' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  // Trial site navigation (role-based, main platform)
  const siteNavItems = [
    { icon: Home, label: 'Dashboard', path: '/site-dashboard' },
    { icon: GraduationCap, label: 'Academy', path: '/academy' },
    { icon: Search, label: 'Referrals', path: '/referrals/review' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  // Sponsor navigation
  const sponsorNavItems = [
    { icon: Home, label: 'Command', path: '/sponsor/command-center' },
    { icon: GraduationCap, label: 'Academy', path: '/academy' },
    { icon: Search, label: 'Sites', path: '/sponsor-site-search' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  // Default navigation for unauthenticated users
  const defaultNavItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Search, label: 'Trials', path: '/trials' },
    { icon: Heart, label: 'Register', path: '/register' },
    { icon: User, label: 'Sign In', path: '/auth' },
  ];

  // Determine which navigation to show based on site context FIRST, then user role
  const getNavItems = () => {
    // PRIORITY 1: White-label site context overrides user role
    if (site?.type === 'cro') {
      return croNavItems;
    }
    
    if (site?.type === 'trial_site') {
      return trialSiteNavItems;
    }

    // PRIORITY 2: Fall back to role-based navigation on main platform
    if (!user) return defaultNavItems;
    
    const role = userProfile?.role?.toLowerCase();
    
    switch (role) {
      case 'patient_caregiver':
        return patientNavItems;
      case 'trial_site':
      case 'medical_professional':
      case 'healthcare_provider':
        return siteNavItems;
      case 'sponsor':
      case 'sponsor_service_provider':
      case 'cro':
        return sponsorNavItems;
      case 'admin':
      case 'super_admin':
        // Admin users on main platform get sponsor-style nav (command center access)
        return sponsorNavItems;
      default:
        return defaultNavItems;
    }
  };

  const navItems = getNavItems();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('?')[0]);
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-lg pb-safe">
      <div className="grid grid-cols-4 h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center justify-center gap-1 min-h-[44px] transition-colors py-2",
                active 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className={cn("h-5 w-5", active && "fill-current")} />
              <span className="text-[10px] font-medium leading-tight">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
